import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { PlatformStoresService } from '../api/platform/services/platform-stores.service';

import type { CsvFeedPreviewResult } from './csv-feed-preview-result.type';

@Injectable({ providedIn: 'root' })
export class CsvFeedPreviewService {
  constructor(private readonly platformStoresService: PlatformStoresService) {}

  async previewFeed(url: string, storeId?: string): Promise<CsvFeedPreviewResult> {
    if (url.startsWith('gs://')) {
      if (!storeId?.trim()) {
        throw new Error('Store ID is required to preview uploaded catalog files');
      }

      return firstValueFrom(
        this.platformStoresService.storeCatalogsControllerPreviewCatalog({
          catalogUrl: url,
          storeId,
        })
      );
    }

    let response: Response;
    try {
      response = await fetch(url, { mode: 'cors' });
    } catch {
      throw new Error(
        'Could not fetch feed (network/CORS). Ensure the URL is publicly accessible.'
      );
    }

    if (!response.ok) {
      throw new Error(`Feed URL returned HTTP ${response.status}`);
    }

    const text = await response.text();
    return this.parseFeedText(text);
  }

  private parseFeedText(text: string): CsvFeedPreviewResult {
    if (!text.trim()) {
      throw new Error('Feed URL returned empty content');
    }

    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
    if (lines.length < 2) {
      throw new Error('Feed must include a header row and at least one data row');
    }

    const delimiter = detectDelimiter(lines[0] ?? '');
    const headers = parseCsvLine(lines[0] ?? '', delimiter).filter((header) => header !== '');

    if (headers.length === 0) {
      throw new Error('Could not parse CSV headers from feed');
    }

    return {
      delimiter,
      headers,
      sampleRowCount: lines.length - 1,
    };
  }
}

function detectDelimiter(firstLine: string): ',' | '\t' {
  const tabCount = (firstLine.match(/\t/g) ?? []).length;
  const commaCount = (firstLine.match(/,/g) ?? []).length;
  return tabCount > commaCount ? '\t' : ',';
}

function parseCsvLine(line: string, delimiter: ',' | '\t'): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  let index = 0;

  while (index < line.length) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 2;
        continue;
      }
      inQuotes = !inQuotes;
      index += 1;
      continue;
    }

    if (char === delimiter && !inQuotes) {
      values.push(current.trim());
      current = '';
      index += 1;
      continue;
    }

    current += char;
    index += 1;
  }

  values.push(current.trim());
  return values;
}
